import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import {
  Send, ArrowUpRight, Lock, ListOrdered, Sparkles, MessageCircle, Info,
} from "lucide-react";

// ---------- Unimora AI chat palette (local — surface-specific) ----------
const NAVY = "#0F1F3D";
const INDIGO = "#4F46E5";
const VIOLET = "#7C3AED";

const SUGGESTIONS = [
  "What is the best match for me?",
  "Compare my shortlisted universities",
  "What scholarships am I eligible for?",
];

const DEMO_RESPONSE = "For a 3-year UK undergraduate programme, the realistic all-in budget for an Indian family typically lands between ₹85L and ₹1.6Cr. Tuition for international students runs ₹22-32L per year at most top-50 universities, with London-based institutions toward the upper end. Living costs add ₹14-22L per year — London is meaningfully more expensive than Manchester, Edinburgh or Birmingham. Scholarships are limited and rarely cover more than 20-30% for international undergraduates. The Graduate Route visa (2-year PSW) materially improves career ROI, especially if you target sectors with active sponsorship.";

// Section card styling, keyed by leading emoji marker
const SECTION_VARIANTS = {
  "🎯": { kind: "decision-headline", card: "bg-[var(--s900)] text-white border-[#0F1F3D]" },
  "✅": { kind: "strengths",         card: "bg-emerald-50 border-emerald-200 text-[var(--success)]" },
  "⚠️": { kind: "risks",             card: "bg-amber-50 border-amber-200 text-[var(--warning)]" },
  "🔄": { kind: "alternative",       card: "bg-slate-50 border-slate-200 text-[var(--s800)]" },
  "💰": { kind: "cost",              card: "bg-white border-slate-200" },
  "📄": { kind: "cta",               card: "" },
  "⚡": { kind: "verdict",           card: "bg-indigo-50 border-indigo-200 text-[var(--ind)]" },
  "🏆": { kind: "winner",            card: "bg-white border-[#4F46E5]/40 ring-1 ring-[#4F46E5]/15" },
  "⚖️": { kind: "tradeoff",          card: "bg-amber-50 border-amber-200 text-[var(--warning)]" },
  "📊": { kind: "differences",       card: "bg-white border-slate-200" },
};
const SECTION_EMOJIS = Object.keys(SECTION_VARIANTS);

// ---------- Helpers ----------
function detectMode(text) {
  const head = text.slice(0, 250);
  if (/MODE\s*1/i.test(head)) return "info";
  if (/MODE\s*2/i.test(head)) return "compare";
  if (/MODE\s*3/i.test(head)) return "decision";
  if (text.includes("🎯") && /Decision Score/i.test(text)) return "decision";
  if (text.includes("⚡") && /Quick Verdict/i.test(text)) return "compare";
  return "info";
}

function parseDecisionMeta(text) {
  const score = text.match(/Decision Score:\s*([\d.]+)\s*\/\s*10/i);
  const conf = text.match(/Confidence:\s*((?:High|Medium|Low)(?:[-–](?:High|Medium|Low))?)/i);
  return { score: score ? score[1] : null, confidence: conf ? conf[1] : null };
}

function splitIntoBlocks(text) {
  const cleaned = text.replace(/^\s*\*{0,2}MODE\s*\d.*?$/im, "").trim();
  // A section starts with one of our emoji markers, optionally preceded by a
  // markdown heading (#, ##, ###, ####) or bold (**…**) wrapper.
  const pattern = /^\s*(?:#{1,4}\s*)?(?:\*{1,2}\s*)?(🎯|✅|⚠️|🔄|💰|📄|⚡|🏆|⚖️|📊)/gmu;
  const indices = [];
  let m;
  while ((m = pattern.exec(cleaned)) !== null) {
    indices.push({ start: m.index, emoji: m[1] });
  }
  if (indices.length === 0) {
    return [{ emoji: null, variant: null, content: cleaned }];
  }
  const blocks = [];
  if (indices[0].start > 0) {
    const pre = cleaned.slice(0, indices[0].start).trim();
    if (pre) blocks.push({ emoji: null, variant: null, content: pre });
  }
  for (let i = 0; i < indices.length; i++) {
    const start = indices[i].start;
    const end = i + 1 < indices.length ? indices[i + 1].start : cleaned.length;
    let segment = cleaned.slice(start, end).trim();
    segment = segment.replace(/\n\s*---+\s*$/, "").trim();
    blocks.push({
      emoji: indices[i].emoji,
      variant: SECTION_VARIANTS[indices[i].emoji],
      content: segment,
    });
  }
  return blocks;
}

// ---------- Atom components ----------
function ModeBadge({ mode }) {
  const map = {
    info:     { label: "Information", cls: "bg-indigo-100 text-[var(--ind)] ring-indigo-200" },
    compare:  { label: "Comparison",  cls: "bg-violet-100 text-[var(--violet)] ring-violet-200" },
    decision: { label: "Decision",    cls: "bg-[var(--s900)] text-[var(--warning)] ring-amber-300/30" },
  };
  const s = map[mode];
  if (!s) return null;
  return (
    <span
      data-testid={`coach-mode-${mode}`}
      className={`inline-flex items-center gap-1.5 text-micro font-semibold uppercase tracking-[0.05em] px-3 py-1 rounded-full ring-1 ${s.cls}`}
    >
      <Sparkles size={11} strokeWidth={2.4} /> Mode · {s.label}
    </span>
  );
}

function ConfidencePill({ confidence }) {
  if (!confidence) return null;
  // Treat hyphenated values like "Medium-High" by taking the highest tier mentioned.
  const norm = confidence.toLowerCase();
  let tier = "Medium";
  if (norm.includes("high")) tier = "High";
  else if (norm.includes("low")) tier = "Low";
  const map = {
    High: "bg-emerald-100 text-[var(--success)] ring-emerald-200",
    Medium: "bg-amber-100 text-[var(--warning)] ring-amber-200",
    Low: "bg-rose-100 text-[var(--danger)] ring-rose-200",
  }[tier];
  return (
    <span
      data-testid={`coach-confidence-${tier.toLowerCase()}`}
      className={`inline-flex items-center text-micro font-semibold uppercase tracking-[0.05em] px-2.5 py-1 rounded-full ring-1 ${map}`}
    >
      {confidence} Confidence
    </span>
  );
}

function DecisionScoreBadge({ score }) {
  if (!score) return null;
  const n = parseFloat(score);
  const ring = n >= 8 ? "ring-emerald-400" : n >= 6 ? "ring-amber-400" : "ring-rose-400";
  return (
    <div
      data-testid="coach-decision-score"
      className={`shrink-0 w-[68px] h-[68px] rounded-full bg-white ring-4 ${ring} shadow-md flex flex-col items-center justify-center`}
    >
      <span className="text-h2 font-bold text-[var(--s900)] leading-none">{score}</span>
      <span className="text-micro uppercase tracking-[0.05em] text-[var(--s500)] mt-0.5">/ 10</span>
    </div>
  );
}

// ---------- Markdown renderers ----------
const MD = {
  h1: ({ children }) => <h1 className="text-h2 font-semibold text-[var(--s900)] mt-3 mb-2">{children}</h1>,
  h2: ({ children }) => <h2 className="text-h3 font-semibold text-[var(--s900)] mt-3 mb-2">{children}</h2>,
  h3: ({ children }) => <h3 className="text-label font-semibold uppercase tracking-[0.05em] text-[var(--ind)] mt-3 mb-2">{children}</h3>,
  h4: ({ children }) => <h4 className="text-label font-semibold uppercase tracking-[0.05em] text-[var(--s500)] mt-3 mb-1.5">{children}</h4>,
  strong: ({ children }) => <strong className="font-semibold text-[var(--s900)]">{children}</strong>,
  em: ({ children }) => <em className="italic text-[var(--s700)]">{children}</em>,
  p: ({ children }) => <p className="text-body-sm leading-relaxed my-2">{children}</p>,
  hr: () => <hr className="my-4 border-0 border-t border-slate-200" />,
  ul: ({ children }) => <ul className="my-2 space-y-1.5 list-none pl-0">{children}</ul>,
  ol: ({ children }) => <ol className="my-2 space-y-1.5 list-decimal pl-5 marker:text-[var(--ind)] marker:font-semibold">{children}</ol>,
  li: ({ children, ordered }) =>
    ordered ? (
      <li className="text-body-sm leading-relaxed pl-1">{children}</li>
    ) : (
      <li className="text-body-sm leading-relaxed pl-5 relative">
        <span className="absolute left-0 top-[9px] w-1.5 h-1.5 rounded-full bg-[var(--ind)]" aria-hidden />
        {children}
      </li>
    ),
  table: ({ children }) => (
    <div className="my-4 overflow-x-auto rounded-[var(--r12)] border border-slate-200 shadow-sm">
      <table className="w-full text-body-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-[var(--ind)] text-white">{children}</thead>,
  tbody: ({ children }) => <tbody className="divide-y divide-slate-200">{children}</tbody>,
  tr: ({ children }) => <tr className="even:bg-slate-50">{children}</tr>,
  th: ({ children }) => <th className="text-left px-3 py-2 font-semibold text-micro uppercase tracking-[0.05em]">{children}</th>,
  td: ({ children }) => <td className="px-3 py-2.5 align-top text-[var(--s700)]">{children}</td>,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noreferrer" className="text-[var(--ind)] underline decoration-1 underline-offset-2 hover:text-[var(--violet)]">{children}</a>
  ),
  code: ({ children }) => <code className="px-1.5 py-0.5 rounded bg-slate-100 text-body-sm text-[var(--s900)]">{children}</code>,
  blockquote: ({ children }) => (
    <blockquote className="my-3 pl-4 border-l-2 border-[#4F46E5]/40 text-[var(--s700)] italic">{children}</blockquote>
  ),
};

function MarkdownView({ children, components }) {
  return (
    <div className="text-[var(--s700)]">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components || MD}>
        {children}
      </ReactMarkdown>
    </div>
  );
}

// Markdown overrides used inside the navy "🎯 Decision Headline" card.
const MD_ON_NAVY = {
  ...MD,
  h1: ({ children }) => <h1 className="text-h2 font-semibold text-white mb-1">{children}</h1>,
  h2: ({ children }) => <h2 className="text-h2 font-semibold text-white mb-1">{children}</h2>,
  h3: ({ children }) => <h3 className="text-micro font-semibold uppercase tracking-[0.05em] text-[var(--warning)] mb-1">{children}</h3>,
  strong: ({ children }) => <strong className="font-semibold text-white">{children}</strong>,
  p: ({ children }) => <p className="text-body-sm leading-relaxed text-white/90 my-1">{children}</p>,
};

// ---------- Block renderer ----------
function Block({ block, decisionMeta }) {
  const v = block.variant;
  // Plain block (no emoji marker) — render as soft markdown text
  if (!v) {
    return (
      <div className="px-1">
        <MarkdownView>{block.content}</MarkdownView>
      </div>
    );
  }
  // 📄 → CTA button
  if (v.kind === "cta") {
    const raw = block.content.replace(/^.*?📄\s*/s, "").replace(/[\[\]→]/g, "").trim();
    const label = raw.split("\n")[0].trim() || "View Full Decision Report";
    return (
      <div className="pt-1">
        <button
          data-testid="coach-cta-button"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-[var(--r12)] text-body-sm font-semibold text-white shadow-md hover:shadow-lg transition-all"
          style={{ background: INDIGO }}
          onClick={(e) => e.preventDefault()}
        >
          {label} <ArrowUpRight size={15} strokeWidth={2.4} />
        </button>
      </div>
    );
  }
  // 🎯 → Decision headline navy card with Score badge floated right
  if (v.kind === "decision-headline") {
    return (
      <div className={`relative rounded-[var(--r20)] border ${v.card} px-6 py-5 shadow-md overflow-hidden`}>
        <div className="absolute right-5 top-5">
          <DecisionScoreBadge score={decisionMeta?.score} />
        </div>
        <div className="pr-24">
          <MarkdownView components={MD_ON_NAVY}>
            {block.content}
          </MarkdownView>
        </div>
      </div>
    );
  }
  // Generic emoji card
  return (
    <div
      data-testid={`coach-section-${v.kind}`}
      className={`rounded-[var(--r12)] border px-5 py-4 ${v.card}`}
    >
      <MarkdownView>{block.content}</MarkdownView>
    </div>
  );
}

// ---------- Assistant response shell ----------
function AssistantResponse({ text, idx }) {
  if (!text) {
    return (
      <div className="text-[var(--s400)] text-body-sm italic flex items-center gap-2">
        <span className="w-1.5 h-1.5 rounded-full bg-[var(--ind)] animate-pulse" />
        Thinking…
      </div>
    );
  }
  const mode = detectMode(text);
  const blocks = splitIntoBlocks(text);
  const meta = mode === "decision" ? parseDecisionMeta(text) : null;
  return (
    <div data-testid={`msg-assistant-${idx}`}>
      <div className="flex items-center gap-2 mb-3">
        <ModeBadge mode={mode} />
        {meta?.confidence && <ConfidencePill confidence={meta.confidence} />}
      </div>
      <div className="space-y-3">
        {blocks.map((b, i) => <Block key={i} block={b} decisionMeta={meta} />)}
      </div>
    </div>
  );
}

// ---------- Header: "Powered by GUIDE™" pill with pulsing dot + ⓘ tooltip ----------
function GuidePoweredPill() {
  return (
    <span
      data-testid="coach-guide-pill"
      className="inline-flex items-center gap-2 text-micro font-semibold uppercase tracking-[0.05em] pl-2.5 pr-1.5 py-1 rounded-full border border-indigo-200 bg-indigo-50 text-[var(--ind)]"
    >
      <span className="relative flex h-2 w-2" aria-hidden>
        <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--ind)] opacity-60 animate-ping" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-[var(--ind)]" />
      </span>
      <span>Powered by GUIDE™</span>
      <span className="relative group inline-flex">
        <button
          type="button"
          aria-label="What is GUIDE™?"
          data-testid="coach-guide-info"
          className="w-5 h-5 rounded-full bg-white border border-indigo-200 text-[var(--ind)] inline-flex items-center justify-center hover:bg-[var(--ind)] hover:text-white transition-colors"
        >
          <Info size={11} strokeWidth={2.4} />
        </button>
        <span
          role="tooltip"
          className="pointer-events-none opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity absolute left-1/2 top-full mt-2 -translate-x-1/2 z-50 w-[300px] rounded-[var(--r12)] bg-[var(--s900)] text-white shadow-xl p-4 text-left normal-case tracking-normal"
        >
          <span className="block text-body font-semibold text-white mb-1.5">
            GUIDE™ — How it works
          </span>
          <span className="block text-label leading-relaxed text-white/85 font-normal">
            GUIDE™ is Unimora AI&apos;s proprietary Global University Decision and Intelligence engine. Unlike generic AI chatbots, GUIDE is your personalized mentor that understands your academic profile, financial constraints, career goals, and previous decisions before making recommendations.
          </span>
          <span className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-[var(--s900)] rotate-45" aria-hidden />
        </span>
      </span>
    </span>
  );
}

// Color-coded pill styles for the new context banner
const PILL_STYLES = {
  IB:        { wrap: "bg-[var(--ind-soft)] border-indigo-100",  label: "text-[var(--ind)]",   value: "text-[var(--ind)]"  },
  Studying:  { wrap: "bg-slate-100 border-slate-200",   label: "text-[var(--s400)]",    value: "text-[var(--s800)]"  },
  Budget:    { wrap: "bg-[var(--success-soft)] border-emerald-100", label: "text-[var(--success)]",  value: "text-[var(--success)]" },
  Targeting: { wrap: "bg-[var(--warning-soft)] border-amber-100",   label: "text-[var(--warning)]",    value: "text-[var(--warning)]"  },
};

// ---------- Profile context banner ----------
function ProfileBanner({ user }) {
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    if (!user) return;
    api.get("/profile").then(({ data }) => setProfile(data)).catch(() => {});
  }, [user]);
  if (!user) return null;
  const pills = [];
  if (profile?.predicted_ib_score) pills.push(["IB", `${profile.predicted_ib_score} / 45`]);
  if (profile?.intended_courses?.length) pills.push(["Studying", profile.intended_courses.slice(0, 2).join(", ")]);
  if (profile?.budget_inr_max) {
    const bmin = profile.budget_inr_min || 0;
    const bmax = profile.budget_inr_max;
    pills.push(["Budget", `₹${(bmin / 100000) | 0}L–₹${(bmax / 100000) | 0}L`]);
  }
  if (profile?.target_countries?.length) {
    pills.push(["Targeting", profile.target_countries.slice(0, 4).join(" · ")]);
  }
  return (
    <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="coach-profile-banner">
      {pills.map(([k, v]) => {
        const s = PILL_STYLES[k] || PILL_STYLES.Studying;
        return (
          <span
            key={k}
            data-testid={`coach-ctx-${k.toLowerCase()}`}
            className={`inline-flex items-center text-label rounded-full border ${s.wrap}`}
          >
            <span className={`text-micro font-semibold uppercase tracking-[0.05em] pl-3 pr-2 py-1.5 ${s.label}`}>{k}</span>
            <span className="text-[var(--s300)] select-none">|</span>
            <span className={`font-semibold pl-2 pr-3 py-1.5 ${s.value}`}>{v}</span>
          </span>
        );
      })}
    </div>
  );
}

// ---------- Main page ----------
export default function Advisor() {
  const { user } = useAuth();
  const [sessionId] = useState(() => {
    const saved = localStorage.getItem("advisor_session_id");
    if (saved) return saved;
    const id = "s_" + Math.random().toString(36).slice(2, 10);
    localStorage.setItem("advisor_session_id", id);
    return id;
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const endRef = useRef(null);

  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await api.get(`/chat/history/${sessionId}`);
      setMessages(data);
    })();
  }, [sessionId, user]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");

    if (!user) {
      setMessages(m => [...m, { role: "user", content: msg }, { role: "assistant", content: DEMO_RESPONSE }]);
      return;
    }

    setMessages(m => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: msg }),
      });
      if (!res.ok || !res.body) throw new Error("stream failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const raw = line.slice(6);
            if (raw === "[DONE]") continue;
            let chunk = "";
            try {
              const parsed = JSON.parse(raw);
              chunk = parsed.t || "";
            } catch {
              if (raw.startsWith("[error]")) continue;
              chunk = raw;
            }
            if (!chunk) continue;
            setMessages(m => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + chunk };
              return copy;
            });
          }
        }
      }
    } catch {
      setMessages(m => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "(Unimora AI Mentor is momentarily unavailable. Please try again.)" };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const isEmpty = messages.length === 0;

  return (
    <div className="min-h-screen bg-white flex flex-col lg:pl-[240px]">
      <Nav />
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10 flex-1 w-full flex flex-col">
        {/* Hero — Unimora AI Mentor artwork as soft background */}
        <section className="relative rounded-[var(--r24)] overflow-hidden border border-indigo-100 bg-gradient-to-br from-[var(--ind-soft)] via-indigo-50 to-white p-7 md:p-10 mb-8">
          {/* Artwork layer */}
          <div
            aria-hidden
            className="absolute right-0 bottom-0 w-[55%] sm:w-[50%] h-full opacity-[0.22] sm:opacity-[0.26] pointer-events-none"
            style={{
              backgroundImage: "url('/cards/advisor-hero.webp')",
              backgroundSize: "contain",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right bottom",
              maskImage: "linear-gradient(to left, black 55%, transparent 100%)",
              WebkitMaskImage: "linear-gradient(to left, black 55%, transparent 100%)",
            }}
          />
          {/* Left-to-right protection gradient — guarantees title/description legibility */}
          <span
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{ background: "linear-gradient(90deg, rgba(255,255,255,0.65) 0%, rgba(255,255,255,0.35) 40%, transparent 100%)" }}
          />

          {/* Content */}
          <div className="relative">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-micro font-semibold uppercase tracking-[0.05em] text-[var(--s500)]">Unimora AI Mentor</span>
              <GuidePoweredPill />
            </div>
            <h1
              className="mt-3 text-h1 text-[var(--s900)] max-w-2xl"
              data-testid="coach-headline"
            >
              An advisor that already knows <em className="italic text-[var(--ind)]">your story.</em>
            </h1>
            <p className="text-body text-[var(--s600)] mt-3 max-w-xl leading-relaxed">
              Ask anything about your shortlists, financials, scholarships, visas — your profile is already loaded.
            </p>

            {/* Profile context banner sits inside the hero */}
            <div className="mt-5">
              <ProfileBanner user={user} />
            </div>
          </div>
        </section>

        {/* Thread */}
        <div className="flex-1 space-y-7 mt-8 mb-8">
          {messages.map((m, i) =>
            m.role === "user" ? (
              <div key={i} className="flex justify-end">
                <div
                  data-testid={`msg-user-${i}`}
                  className="max-w-[72%] rounded-[var(--r20)] px-5 py-3 text-body-sm leading-relaxed shadow-sm"
                  style={{ background: NAVY, color: "#fff" }}
                >
                  {m.content}
                </div>
              </div>
            ) : (
              <div key={i} className="max-w-full relative">
                <div
                  className={`relative rounded-[var(--r20)] border border-slate-200 bg-white p-6 shadow-sm ${!user ? "blur-[6px] select-none pointer-events-none" : ""}`}
                >
                  <AssistantResponse text={m.content} idx={i} />
                </div>
                {!user && (
                  <div className="absolute inset-0 flex items-center justify-center px-4 pt-8">
                    <div className="text-center max-w-md bg-white/95 backdrop-blur-md border border-slate-200 rounded-[var(--r20)] shadow-xl p-6">
                      <div className="w-10 h-10 mx-auto rounded-full bg-indigo-50 text-[var(--ind)] flex items-center justify-center mb-3">
                        <Lock size={17} strokeWidth={1.8} />
                      </div>
                      <h3 className="text-h3 font-semibold text-[var(--s900)] leading-tight">
                        Sign in to see the answer
                      </h3>
                      <p className="text-body-sm text-[var(--s600)] mt-1.5 leading-relaxed">
                        Evidence-backed, never fabricated — built on verified data.
                      </p>
                      <button
                        onClick={startGoogle}
                        data-testid="advisor-unlock"
                        className="inline-flex items-center justify-center gap-2 text-body-sm font-semibold mt-4 px-4 py-2.5 rounded-[var(--r12)] text-white"
                        style={{ background: INDIGO }}
                      >
                        Get Early Access
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )
          )}
          <div ref={endRef} />
        </div>

        {/* Suggested prompts (first load only) */}
        {isEmpty && (
          <div className="mb-3">
            <div className="text-micro font-semibold uppercase tracking-[0.05em] text-[var(--s400)] mb-2">Try asking</div>
            <div className="flex flex-wrap gap-2">
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  onClick={() => send(s)}
                  data-testid={`advisor-sug-${i}`}
                  className="text-body-sm px-3.5 py-2 rounded-full border border-slate-200 bg-white text-[var(--s700)] hover:border-[#4F46E5] hover:text-[var(--ind)] inline-flex items-center gap-1.5 transition-colors"
                >
                  {s}
                  <ArrowUpRight size={13} strokeWidth={2.2} className="opacity-60" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Sticky input bar */}
        <div className="sticky bottom-4">
          <div
            className="flex items-center gap-2 rounded-[var(--r20)] border border-slate-200 bg-white shadow-lg shadow-slate-200/40 p-2 pl-4"
            style={{ borderTopColor: "#E2E8F0" }}
          >
            <MessageCircle size={16} className="text-[var(--s400)] shrink-0" strokeWidth={1.8} />
            <input
              className="flex-1 border-0 focus:border-0 shadow-none bg-transparent text-body-sm placeholder:text-[var(--s400)]"
              style={{ boxShadow: "none", border: 0, outline: "none" }}
              placeholder="Ask anything about your university decision..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") send(); }}
              data-testid="advisor-input"
            />
            <button
              className="inline-flex items-center gap-1.5 text-body-sm font-semibold text-white px-4 py-2.5 rounded-[var(--r12)] disabled:opacity-40 transition-all"
              style={{ background: INDIGO }}
              disabled={streaming || !input.trim()}
              onClick={() => send()}
              data-testid="advisor-send"
            >
              Send <Send size={14} strokeWidth={2.4} />
            </button>
          </div>
        </div>
      </div>
      <ChatScrollThumb messages={messages} />
      <Footer />
    </div>
  );
}

function ChatScrollThumb({ messages }) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);

  const userQueries = (messages || [])
    .map((m, idx) => ({ ...m, idx }))
    .filter(m => m.role === "user");

  const scrollToMsg = (idx) => {
    const el = document.querySelector(`[data-testid="msg-user-${idx}"]`);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 100;
    window.scrollTo({ top, behavior: "smooth" });
  };

  const handleEnter = () => { clearTimeout(closeTimer.current); setOpen(true); };
  const handleLeave = () => { closeTimer.current = setTimeout(() => setOpen(false), 250); };

  if (userQueries.length === 0) return null;

  return (
    <div
      className="fixed right-5 top-1/2 -translate-y-1/2 z-40 hidden lg:block"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="chat-scroll-thumb"
        aria-label="Jump to question"
        className={`w-11 h-11 rounded-full flex items-center justify-center shadow-lg transition-all ${
          open
            ? "bg-[var(--ind)] text-white scale-105"
            : "bg-white text-[var(--s600)] border border-slate-200 hover:text-[var(--ind)] hover:border-[#4F46E5]"
        }`}
      >
        <ListOrdered size={18} strokeWidth={2} />
      </button>

      {open && (
        <div className="absolute right-full top-1/2 -translate-y-1/2 mr-3 w-[320px] max-h-[60vh] overflow-y-auto bg-white border border-slate-200 rounded-[var(--r12)] shadow-2xl p-3">
          <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s500)] mb-2 px-2 flex items-center justify-between">
            <span>Your questions</span>
            <span className="text-[var(--s400)] normal-case tracking-normal">{userQueries.length}</span>
          </div>
          <ul className="space-y-0.5">
            {userQueries.map((q) => (
              <li key={q.idx}>
                <button
                  onClick={() => { scrollToMsg(q.idx); setOpen(false); }}
                  data-testid={`scroll-jump-${q.idx}`}
                  className="w-full text-left text-body-sm text-[var(--s700)] hover:bg-indigo-50 hover:text-[var(--ind)] rounded-[var(--r8)] px-2.5 py-2 transition-colors line-clamp-2"
                >
                  {q.content}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
